/**
 * GroupInfoPanel Component
 * 
 * Side panel (desktop) or full-screen overlay (mobile) for managing groups:
 * - View/edit group name
 * - View participant list
 * - Add new members
 * - Remove members (creator only) - always visible on mobile, hover on desktop
 * - Leave group
 */

import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { 
  X, 
  Users, 
  Edit2, 
  Check, 
  UserPlus,
  UserMinus,
  LogOut, 
  Loader2,
  Search,
  Crown,
  ChevronLeft
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '@/components/ui/dialog';
import { 
  updateGroupName, 
  addParticipant,
  removeParticipant,
  leaveConversation,
  CONVERSATION_TYPES,
  getUserProfile
} from '@/lib/firestore';
import { UserAvatar } from '@/components/shared';
import { useAvatarCache } from '@/contexts';
import { useDesign } from '@/contexts/DesignContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to check if string looks like an email
const looksLikeEmail = (str) => str && str.includes('@') && str.includes('.');

export default function GroupInfoPanel({ 
  conversation, 
  currentUser, 
  onClose,
  onConversationUpdated,
  onLeaveGroup
}) {
  const { isStudio } = useDesign();
  const [isEditingName, setIsEditingName] = useState(false);
  const [newName, setNewName] = useState('');
  const [savingName, setSavingName] = useState(false);
  
  // Add member state
  const [showAddMember, setShowAddMember] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [availableMembers, setAvailableMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [addingMember, setAddingMember] = useState(null);
  const addMemberSearchRef = useRef(null);
  
  // Remove member state
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState(null);
  const [removingMember, setRemovingMember] = useState(false);
  
  // Leave group state
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [leaving, setLeaving] = useState(false);
  
  // Track resolved names (fetched from Firestore users collection)
  const [resolvedNames, setResolvedNames] = useState({});
  const resolvedConversationIdRef = React.useRef(null);

  const isGroup = conversation?.type === CONVERSATION_TYPES.GROUP;
  const participants = conversation?.participants || [];
  const participantDetails = conversation?.participantDetails || {};
  const isCreator = conversation?.createdBy === currentUser?.id;

  // Fetch correct names from Firestore users collection for names that look like emails
  // Only fetch once per conversation ID
  useEffect(() => {
    const conversationId = conversation?.id;
    if (!conversationId || resolvedConversationIdRef.current === conversationId) {
      return;
    }
    
    // Mark that we're fetching for this conversation
    resolvedConversationIdRef.current = conversationId;
    
    const fetchNames = async () => {
      const participantsList = conversation?.participants || [];
      const details = conversation?.participantDetails || {};
      const resolved = {};
      
      for (const userId of participantsList) {
        const storedName = details[userId]?.name;
        
        if (looksLikeEmail(storedName)) {
          try {
            const profile = await getUserProfile(userId);
            if (profile?.name && !looksLikeEmail(profile.name)) {
              resolved[userId] = {
                name: profile.name,
                avatar: profile.avatar
              };
            }
          } catch (err) {
            // Keep original if fetch fails
          }
        }
      }
      
      if (Object.keys(resolved).length > 0) {
        setResolvedNames(resolved);
      }
    };
    
    fetchNames();
  }, [conversation?.id]);

  // Helper to get participant info - prefers resolved names over stored names
  // Avatar uses cache (MongoDB-sourced) first, Firestore as fallback
  const { getCachedAvatar } = useAvatarCache();
  const getParticipantInfo = (userId) => {
    const resolved = resolvedNames[userId];
    const stored = participantDetails[userId];
    
    return {
      name: resolved?.name || stored?.name || 'Unknown',
      avatar: getCachedAvatar(userId) || resolved?.avatar || stored?.avatar
    };
  };

  // Initialize name editing
  useEffect(() => {
    if (conversation?.name) {
      setNewName(conversation.name);
    }
  }, [conversation?.name]);

  // Fetch available church team members for adding (church-scoped, not all users)
  const fetchAvailableMembers = async () => {
    setLoadingMembers(true);
    try {
      const response = await fetch(`${API_URL}/api/user/team/${currentUser.church_id}`);
      if (!response.ok) throw new Error('Failed to fetch team members');
      const data = await response.json();
      const available = (data.members || []).filter(
        member => !participants.includes(member.id) && member.id !== currentUser?.id
      );
      setAvailableMembers(available);
    } catch (err) {
      console.error('Error fetching team members:', err);
    } finally {
      setLoadingMembers(false);
    }
  };

  // Fetch members when add member dialog opens
  useEffect(() => {
    if (showAddMember) {
      fetchAvailableMembers();
    } else {
      setSearchQuery('');
      setAvailableMembers([]);
    }
  }, [showAddMember]);

  // Filter available members by search
  const filteredMembers = availableMembers.filter(member => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    const name = (member.name || member.email || '').toLowerCase();
    return name.includes(query);
  });

  // Handle saving group name
  const handleSaveName = async () => {
    if (!newName.trim() || newName.trim() === conversation.name) {
      setIsEditingName(false);
      return;
    }

    setSavingName(true);
    try {
      await updateGroupName(conversation.id, newName.trim(), {
        id: currentUser.id,
        name: currentUser.name || currentUser.user_metadata?.name || currentUser.user_metadata?.full_name || currentUser.email
      });
      setIsEditingName(false);
      if (onConversationUpdated) onConversationUpdated();
    } catch (err) {
      console.error('Error updating group name:', err);
      toast.error("Couldn't rename this group. Please try again.");
    } finally {
      setSavingName(false);
    }
  };

  // Handle adding a member
  const handleAddMember = async (member) => {
    setAddingMember(member.id);
    try {
      await addParticipant(conversation, member, {
        id: currentUser.id,
        name: currentUser.name || currentUser.user_metadata?.name || currentUser.user_metadata?.full_name || currentUser.email
      });
      // Keep the dialog open for rapid multi-add: remove the just-added member
      // from the list, reset the search, and refocus it so the user can
      // immediately type the next name.
      setAvailableMembers(prev => prev.filter(m => m.id !== member.id));
      setSearchQuery('');
      addMemberSearchRef.current?.focus();
      if (onConversationUpdated) onConversationUpdated();
    } catch (err) {
      console.error('Error adding member:', err);
      toast.error("Couldn't add this member. Please try again.");
    } finally {
      setAddingMember(null);
    }
  };

  // Open remove confirmation dialog
  const handleRemoveClick = (userId) => {
    const userInfo = getParticipantInfo(userId);
    setMemberToRemove({
      id: userId,
      name: userInfo.name
    });
    setShowRemoveConfirm(true);
  };

  // Handle removing a member
  const handleRemoveMember = async () => {
    if (!memberToRemove) return;
    
    setRemovingMember(true);
    try {
      await removeParticipant(conversation.id, memberToRemove, {
        id: currentUser.id,
        name: currentUser.name || currentUser.user_metadata?.name || currentUser.user_metadata?.full_name || currentUser.email
      });
      setShowRemoveConfirm(false);
      setMemberToRemove(null);
      if (onConversationUpdated) onConversationUpdated();
    } catch (err) {
      console.error('Error removing member:', err);
      toast.error('Couldn\'t remove this member. Please try again.');
    } finally {
      setRemovingMember(false);
    }
  };

  // Handle leaving group
  const handleLeaveGroup = async () => {
    setLeaving(true);
    try {
      await leaveConversation(conversation, {
        id: currentUser.id,
        name: currentUser.name || currentUser.user_metadata?.name || currentUser.user_metadata?.full_name || currentUser.email
      });
      setShowLeaveConfirm(false);
      if (onLeaveGroup) onLeaveGroup();
    } catch (err) {
      console.error('Error leaving group:', err);
      toast.error("Couldn't leave this group. Please try again.");
    } finally {
      setLeaving(false);
    }
  };

  if (!isGroup) {
    // Direct message info
    const otherUserId = participants.find(id => id !== currentUser?.id);
    const otherUser = participantDetails[otherUserId];
    const otherUserAvatar = getCachedAvatar(otherUserId) || otherUser?.avatar;

    return (
      <>
        {/* Mobile: Full-screen overlay */}
        <div data-ui="info-sheet" className="md:hidden fixed inset-0 z-50 bg-card flex flex-col">
          {/* Header */}
          <div data-ui="info-head" className="flex items-center gap-2 px-4 py-3 border-b border-slate-200 dark:border-white/10">
            <Button variant="ghost" size="icon" onClick={onClose} className="flex-shrink-0">
              <ChevronLeft className="w-6 h-6" />
            </Button>
            <h3 className="font-semibold text-slate-800 dark:text-slate-100 flex-1">Details</h3>
          </div>

          {/* User Info */}
          <div className="flex-1 p-6">
            <div className="flex flex-col items-center text-center">
              <UserAvatar 
                userId={otherUserId}
                name={otherUser?.name} 
                avatarUrl={otherUserAvatar} 
                size="xl" 
              />
              <h4 data-ui="ident-name" className="mt-4 font-semibold text-xl text-slate-800 dark:text-slate-100">
                {otherUser?.name || 'Unknown'}
              </h4>
              <p data-ui="ident-sub" className="text-sm text-slate-500 dark:text-slate-400 mt-1">{isStudio ? 'Direct message' : 'Direct Message'}</p>
            </div>
          </div>
        </div>

        {/* Desktop: Side panel */}
        <div data-ui="info-panel" className="hidden md:flex w-80 border-l border-slate-200 dark:border-white/10 bg-card flex-col">
          <div data-ui="info-head" className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-white/10">
            <h3 data-ui="info-title" className="font-semibold text-slate-800 dark:text-slate-100">Details</h3>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="w-5 h-5" />
            </Button>
          </div>

          <div className="flex-1 p-4">
            <div className="flex flex-col items-center text-center mb-6">
              <UserAvatar 
                userId={otherUserId}
                name={otherUser?.name} 
                avatarUrl={otherUserAvatar} 
                size="xl" 
              />
              <h4 data-ui="ident-name" className="mt-3 font-semibold text-lg text-slate-800 dark:text-slate-100">
                {otherUser?.name || 'Unknown'}
              </h4>
              <p data-ui="ident-sub" className="text-sm text-slate-500 dark:text-slate-400">{isStudio ? 'Direct message' : 'Direct Message'}</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Group info content (shared between mobile and desktop)
  const GroupInfoContent = ({ isMobile = false }) => (
    <div className={`${isMobile ? 'p-4' : 'p-4'} space-y-6`}>
      {/* Group Avatar and Name */}
      <div className="flex flex-col items-center text-center">
        <div data-ui="avatar-group" className={`${isMobile ? 'w-20 h-20' : 'w-16 h-16'} rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center`}>
          <Users className={`${isMobile ? 'w-10 h-10' : 'w-8 h-8'} text-white`} />
        </div>
        
        {isEditingName ? (
          <div className="mt-3 flex items-center gap-2 w-full max-w-xs">
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Group name"
              className="text-center"
              maxLength={50}
              autoFocus
            />
            <Button 
              size="icon" 
              onClick={handleSaveName}
              disabled={savingName}
              className="shrink-0"
            >
              {savingName ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Check className="w-4 h-4" />
              )}
            </Button>
          </div>
        ) : (
          <div className="mt-3 flex items-center gap-2">
            <h4 data-ui="ident-name" className={`font-semibold ${isMobile ? 'text-xl' : 'text-lg'} text-slate-800 dark:text-slate-100`}>
              {conversation.name}
            </h4>
            <button
              onClick={() => setIsEditingName(true)}
              data-ui="ibtn-sm"
              className="p-1 hover:bg-slate-100 dark:hover:bg-white/10 rounded-full transition-colors"
              title="Edit group name"
            >
              <Edit2 className="w-4 h-4 text-slate-400 dark:text-slate-500" />
            </button>
          </div>
        )}
        
        <p data-ui="ident-sub" className="text-sm text-slate-500 dark:text-slate-400 mt-1">
          {participants.length} participants
        </p>
      </div>

      {/* Participants List */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h5 data-ui="info-sec-h" className="font-medium text-slate-700 dark:text-slate-200">Members</h5>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowAddMember(true)}
            data-ui="ghost-sm"
            className="text-blue-500 dark:text-blue-400 hover:text-blue-600"
            data-testid="add-member-btn"
          >
            <UserPlus className="w-4 h-4 mr-1" />
            Add
          </Button>
        </div>
        
        <div className="space-y-1">
          {participants.map(userId => {
            const userInfo = getParticipantInfo(userId);
            const isCurrentUser = userId === currentUser?.id;
            const isGroupCreator = userId === conversation.createdBy;
            const canRemove = isCreator && !isGroupCreator && !isCurrentUser;
            
            return (
              <div 
                key={userId}
                data-ui="member-row"
                className={`flex items-center gap-3 ${isMobile ? 'p-3' : 'p-2'} rounded-lg hover:bg-slate-50 dark:hover:bg-white/5 group`}
              >
                <UserAvatar 
                  userId={userId}
                  name={userInfo.name} 
                  avatarUrl={userInfo.avatar} 
                  size={isMobile ? 'md' : 'sm'}
                />
                <div className="flex-1 min-w-0">
                  <p data-ui="member-name" className={`font-medium ${isMobile ? 'text-base' : 'text-sm'} text-slate-800 dark:text-slate-100 truncate`}>
                    {userInfo.name}
                    {isCurrentUser && (
                      <span className="text-slate-400 dark:text-slate-500 font-normal"> (You)</span>
                    )}
                  </p>
                </div>
                
                {/* Crown for creator */}
                {isGroupCreator && (
                  <Crown className="w-4 h-4 text-amber-500 dark:text-amber-400 flex-shrink-0" title="Group creator" />
                )}
                
                {/* Remove button - always visible on mobile, hover on desktop */}
                {canRemove && (
                  <button
                    onClick={() => handleRemoveClick(userId)}
                    className={`
                      ${isMobile ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}
                      p-2 hover:bg-red-100 rounded-full transition-all active:scale-95
                    `}
                    title="Remove from group"
                    data-testid={`remove-member-${userId}`}
                  >
                    <UserMinus className={`${isMobile ? 'w-5 h-5' : 'w-4 h-4'} text-red-500 dark:text-red-400`} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Leave Group */}
      <div className="pt-4 border-t border-slate-100 dark:border-white/10">
        <Button
          variant="ghost"
          data-ui="danger-full"
          className={`w-full text-red-500 dark:text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-500/15 ${isMobile ? 'h-12' : ''}`}
          onClick={() => setShowLeaveConfirm(true)}
          data-testid="leave-group-btn"
        >
          <LogOut className="w-4 h-4 mr-2" />
          {isStudio ? 'Leave group' : 'Leave Group'}
        </Button>
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile: Full-screen overlay */}
      <div data-ui="info-sheet" className="md:hidden fixed inset-0 z-50 bg-card flex flex-col">
        {/* Header */}
        <div data-ui="info-head" className="flex items-center gap-2 px-2 py-3 border-b border-slate-200 dark:border-white/10">
          <Button variant="ghost" size="icon" onClick={onClose} className="flex-shrink-0">
            <ChevronLeft className="w-6 h-6" />
          </Button>
          <h3 data-ui="info-title" className="font-semibold text-lg text-slate-800 dark:text-slate-100 flex-1">{isStudio ? 'Group info' : 'Group Info'}</h3>
        </div>

        {/* Content */}
        <ScrollArea className="flex-1">
          {GroupInfoContent({ isMobile: true })}
        </ScrollArea>
      </div>

      {/* Desktop: Side panel */}
      <div data-ui="info-panel" className="hidden md:flex w-80 border-l border-slate-200 dark:border-white/10 bg-card flex-col">
        {/* Header */}
        <div data-ui="info-head" className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-white/10">
          <h3 data-ui="info-title" className="font-semibold text-slate-800 dark:text-slate-100">{isStudio ? 'Group info' : 'Group Info'}</h3>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        <ScrollArea className="flex-1">
          {GroupInfoContent({ isMobile: false })}
        </ScrollArea>
      </div>

      {/* Add Member Dialog */}
      <Dialog open={showAddMember} onOpenChange={setShowAddMember}>
        <DialogContent className="sm:max-w-md max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus data-ui="dialog-icon-tile" className="w-5 h-5 text-blue-500 dark:text-blue-400" />
              {isStudio ? 'Add member' : 'Add Member'}
            </DialogTitle>
          </DialogHeader>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 dark:text-slate-500" />
            <Input
              ref={addMemberSearchRef}
              type="text"
              placeholder={isStudio ? 'Search users' : 'Search users...'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
              data-testid="add-member-search"
            />
          </div>

          {/* Member List */}
          <ScrollArea className="h-64">
            {loadingMembers ? (
              <div className="flex items-center justify-center h-full">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400 dark:text-slate-500" />
              </div>
            ) : filteredMembers.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-slate-400 dark:text-slate-500">
                <Users className="w-10 h-10 mb-2 opacity-50" />
                <p className="text-sm">
                  {searchQuery ? 'No users found' : 'All users are in this group'}
                </p>
              </div>
            ) : (
              <div className="space-y-1">
                {filteredMembers.map(member => (
                  <button
                    key={member.id}
                    onClick={() => handleAddMember(member)}
                    disabled={addingMember === member.id}
                    data-ui="member-row"
                    className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 dark:hover:bg-white/5 border border-transparent disabled:opacity-50 active:scale-[0.98]"
                    data-testid={`add-user-${member.id}`}
                  >
                    <UserAvatar userId={member.id} name={member.name || member.email} avatarUrl={member.avatar} size="md" />
                    <div className="flex-1 text-left min-w-0">
                      <div className="font-medium text-slate-800 dark:text-slate-100 truncate">
                        {member.name || member.email}
                      </div>
                      {member.name && (
                        <div className="text-sm text-slate-500 dark:text-slate-400 truncate">{member.email}</div>
                      )}
                    </div>
                    {addingMember === member.id ? (
                      <Loader2 className="w-5 h-5 animate-spin text-blue-500 dark:text-blue-400 flex-shrink-0" />
                    ) : (
                      <UserPlus className="w-5 h-5 text-blue-500 dark:text-blue-400 flex-shrink-0" />
                    )}
                  </button>
                ))}
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Remove Member Confirmation Dialog */}
      <Dialog open={showRemoveConfirm} onOpenChange={setShowRemoveConfirm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserMinus data-ui="dialog-icon-tile" className="w-5 h-5 text-red-500 dark:text-red-400" />
              {isStudio ? 'Remove member' : 'Remove Member'}
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to remove <strong>{memberToRemove?.name}</strong> from this group? They will no longer be able to see new messages.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowRemoveConfirm(false);
                setMemberToRemove(null);
              }}
              className="flex-1 sm:flex-none"
            >
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleRemoveMember}
              disabled={removingMember}
              className="flex-1 sm:flex-none"
              data-testid="confirm-remove-btn"
            >
              {removingMember ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Removing...
                </>
              ) : (
                'Remove'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Leave Group Confirmation Dialog */}
      <Dialog open={showLeaveConfirm} onOpenChange={setShowLeaveConfirm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{isStudio ? 'Leave group' : 'Leave Group'}</DialogTitle>
            <DialogDescription>
              Are you sure you want to leave &ldquo;{conversation?.name}&rdquo;? You won&apos;t be able to see new messages unless someone adds you back.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setShowLeaveConfirm(false)} className="flex-1 sm:flex-none">
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleLeaveGroup}
              disabled={leaving}
              className="flex-1 sm:flex-none"
              data-testid="confirm-leave-btn"
            >
              {leaving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Leaving...
                </>
              ) : (
                isStudio ? 'Leave group' : 'Leave Group'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
